'use client';

import type { FC } from 'react';
import { BottomSheet } from '../../ui/BottomSheet';
import { FaTrophy, FaClock, FaDice, IoTicket } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations from './raffle.translations';

interface RaffleRulesSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Ticket-accrual table + antiabuse notes, shared pattern with the games "как играть" sheet. */
export const RaffleRulesSheet: FC<RaffleRulesSheetProps> = ({ isOpen, onClose }) => {
  const { t } = useLocalTranslation(translations);

  const rows: { labelKey: 'ruleRowWin' | 'ruleRowMvp' | 'ruleRowTriple' | 'ruleRowQuad' | 'ruleRowAce' | 'ruleRowOneHp' | 'ruleRowClutch2' | 'ruleRowClutch3'; value: number }[] = [
    { labelKey: 'ruleRowWin', value: 1 },
    { labelKey: 'ruleRowMvp', value: 1 },
    { labelKey: 'ruleRowTriple', value: 1 },
    { labelKey: 'ruleRowQuad', value: 2 },
    { labelKey: 'ruleRowAce', value: 4 },
    { labelKey: 'ruleRowOneHp', value: 2 },
    { labelKey: 'ruleRowClutch2', value: 2 },
    { labelKey: 'ruleRowClutch3', value: 3 },
  ];

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('rulesTitle')} heightClass="h-auto max-h-[88vh]">
      <div className="flex flex-col gap-3 pb-6">
        <div className="overflow-hidden rounded-2xl bg-fill-tertiary">
          {rows.map((row, i) => (
            <div
              key={row.labelKey}
              className={`flex items-center justify-between px-4 py-3 ${i > 0 ? 'border-t border-white/5' : ''}`}
            >
              <span className="text-sm text-label-primary">{t(row.labelKey)}</span>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-accent-blue">
                +{row.value} <IoTicket size={13} />
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-start gap-3 rounded-2xl bg-fill-tertiary p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-xl text-amber-400">
            <FaTrophy />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-label-primary">{t('ruleWinsOnlyTitle')}</h3>
            <p className="mt-1 text-sm leading-normal text-label-secondary">{t('ruleWinsOnlyDescription')}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-2xl bg-fill-tertiary p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-xl text-amber-400">
            <FaClock />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-label-primary">{t('ruleBurnTitle')}</h3>
            <p className="mt-1 text-sm leading-normal text-label-secondary">{t('ruleBurnDescription')}</p>
          </div>
        </div>

        <div className="flex items-start gap-3 rounded-2xl bg-fill-tertiary p-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-xl text-amber-400">
            <FaDice />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold text-label-primary">{t('ruleOneChanceTitle')}</h3>
            <p className="mt-1 text-sm leading-normal text-label-secondary">{t('ruleOneChanceDescription')}</p>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default RaffleRulesSheet;
